import { createRef, ref } from 'lit/directives/ref.js';
import { DataBinding, getInternalId } from './databinding/databinding';
import { DataTracker, FieldType } from './databinding/data-tracker';
import {
  defaultUserUIPreferences,
  PreferredTabPlacement,
  PreferredTabVariant,
  rowsPerPageChoices,
  userUIPreferences
} from '../context/user-preferences';
import { FormInputAssistant } from './templateresult/form-input-assistant';
import { html, TemplateResult } from 'lit';
import { LangAppIndex } from '../../../localization/webmodule-common/other/app-index-lang';
import { LangModalConfirmation } from '../../../localization/webmodule-common/other/ui/modal-confirmation-lang';
import { LitBaseModal } from './modal/modal-factory-lit';
import type { FormInputSelectValue } from './templateresult/form-input-types';
import type { Snippet } from '../../interop/webmodule-interop';

/**
 * The "My Preferences" dialog opened from the user dropdown. Edits the
 * {@link userUIPreferences} store; persistence (local today, backend later) is
 * the store's concern, so this dialog never touches storage directly.
 */
export class UserPreferencesModal extends LitBaseModal {
  private readonly internalId = `user-preferences-${getInternalId()}`;
  private readonly formRef = createRef<HTMLFormElement>();
  private readonly binding = new DataBinding(() => this.formRef.value!, this.internalId);
  private readonly dataTracker = new DataTracker(this.binding);

  private rowsPerPage = userUIPreferences.current.tableRowsPerPage;
  private tabPlacement: PreferredTabPlacement = userUIPreferences.current.tabPlacement;
  private tabVariant: PreferredTabVariant = userUIPreferences.current.tabVariant;

  constructor() {
    super();
    this.dataTracker.addDynamic(
      'rowsPerPage',
      FieldType.int,
      () => this.rowsPerPage,
      value => {
        // A stored value missing from the choices renders the select empty and
        // binds back null; keep the prior value instead of saving Number(null)=0.
        const rows = Number(value);
        if (Number.isInteger(rows) && rows > 0) this.rowsPerPage = rows;
      }
    );
    this.dataTracker.addDynamic(
      'tabPlacement',
      FieldType.string,
      () => this.tabPlacement,
      value => (this.tabPlacement = value as PreferredTabPlacement)
    );
    this.dataTracker.addDynamic(
      'tabVariant',
      FieldType.string,
      () => this.tabVariant,
      value => (this.tabVariant = value as PreferredTabVariant)
    );
  }

  title(): Snippet {
    return LangAppIndex.preferencesTitle();
  }

  protected get modalSize() {
    return 'modal-md';
  }

  public bodyTemplate(): TemplateResult {
    const form = new FormInputAssistant(this.dataTracker, false, false);
    const option = (value: string, text: string): FormInputSelectValue => ({ text, value, disabled: false });

    return html`
      <form ${ref(this.formRef)} id=${this.internalId} class="p-2 user-preferences-form">
        <webmodule-card class="user-preferences-group d-block mb-3">
          <div slot="header" class="user-preferences-group-title">${LangAppIndex.prefSectionTable()}</div>
          ${form.arraySelect('rowsPerPage', [...rowsPerPageChoices], x => option(String(x), String(x)), {
            title: LangAppIndex.prefRowsPerPage()
          })}
        </webmodule-card>
        <webmodule-card class="user-preferences-group d-block mb-3">
          <div slot="header" class="user-preferences-group-title">${LangAppIndex.prefSectionTabs()}</div>
          ${form.radioGroupArray(
            'tabPlacement',
            [option('top', LangAppIndex.prefTabPositionTop()), option('start', LangAppIndex.prefTabPositionSide())],
            { title: LangAppIndex.prefTabPosition() }
          )}
          ${form.radioGroupArray(
            'tabVariant',
            [option('classic', LangAppIndex.prefTabStyleClassic()), option('chip', LangAppIndex.prefTabStyleChip())],
            { title: LangAppIndex.prefTabStyle() }
          )}
        </webmodule-card>
        <div class="form-text text-muted">${LangAppIndex.prefStoredLocally()}</div>
      </form>
    `;
  }

  public footerTemplate(): TemplateResult {
    const saveEvent = async () => {
      if (!(this.formRef.value?.reportValidity() ?? true)) return;
      this.dataTracker.applyChangeToValue();
      await userUIPreferences.update({
        tableRowsPerPage: this.rowsPerPage,
        tabPlacement: this.tabPlacement,
        tabVariant: this.tabVariant
      });
      await this.hideModal();
    };
    const resetEvent = () => {
      this.rowsPerPage = defaultUserUIPreferences.tableRowsPerPage;
      this.tabPlacement = defaultUserUIPreferences.tabPlacement;
      this.tabVariant = defaultUserUIPreferences.tabVariant;
      // push the restored values back into the rendered editors
      this.dataTracker.resetEditorValue();
      this.requestUpdate();
    };

    return html`
      <webmodule-button size="small" variant="default" class="me-auto" @click=${resetEvent}>
        ${LangAppIndex.prefResetToDefaults()}
      </webmodule-button>
      ${this.createConfirmCancelButtons(
        LangModalConfirmation.saveAndClose(),
        saveEvent,
        LangModalConfirmation.cancel()
      )}
    `;
  }
}
